// React Imports
import { useState } from "react";
import { useNavigate } from "react-router";

// Local Components
import { LoginLayout } from "../LoginLayout/LoginLayout";

// External Libraries
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";

// Hooks & Helpers
import { forgotPassword, confirmNewPassword } from "sj-ui-toolkit/src/integrations/cognito/cognito";

// Styles
import S from "./ForgotPasswordPage.module.scss";

// Routes
import { LoginAppRoutes } from "sj-ui-toolkit/src/constants/routes";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [isRequestInFlight, setIsRequestInFlight] = useState(false);
  const [isResetCodeSent, setIsResetCodeSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const navigate = useNavigate();

  const handleForgotPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      setIsRequestInFlight(true);
      await forgotPassword(email);
      window.toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "If the email is registered, a password reset code has been sent.",
      });
      setIsResetCodeSent(true);
    } catch (error) {
      console.error("Error during password reset:", error);
      window.toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to send password reset code.",
      });
    } finally {
      setIsRequestInFlight(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match");
      return;
    }

    setPasswordError("");

    try {
      setIsRequestInFlight(true);
      await confirmNewPassword(email, verificationCode, newPassword);
      window.toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Your password has been reset successfully.",
      });
      navigate(LoginAppRoutes.route);
    } catch (error) {
      console.error("Error during password reset confirmation:", error);
      window.toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setIsRequestInFlight(false);
    }
  };

  return (
    <LoginLayout title={!isResetCodeSent ? "Forgot Password" : "Reset Password"}>
      {!isResetCodeSent && (
        <form
          onSubmit={handleForgotPassword}
          className={`p-fluid ${S.formContainer}`}
          data-testid="forgot-password-request-form"
        >
          <p>Please enter your email address to reset your password.</p>
          <InputText
            name="email"
            data-testid="forgot-password-email-input"
            aria-label="Enter your email"
            required
            className="w-full"
            type="email"
            placeholder="Email"
            autoComplete="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
          <div className={S.buttonContainer}>
            <Button
              className={S.actionButton}
              data-testid="forgot-password-back-button"
              severity="secondary"
              onClick={() => navigate(LoginAppRoutes.route)}
              label="Back to Login"
              type="button"
            />
            <Button
              loading={isRequestInFlight}
              disabled={isRequestInFlight}
              type="submit"
              data-testid="forgot-password-send-button"
              className={S.actionButton}
              label="Send Email"
            />
          </div>
        </form>
      )}
      {isResetCodeSent && (
        <form
          onSubmit={handleResetPassword}
          className={`p-fluid ${S.formContainer}`}
          data-testid="forgot-password-reset-form"
        >
          <p>Please enter the verification code sent to your email:</p>
          <InputText
            className="w-full"
            type="text"
            placeholder="Verification Code"
            data-testid="forgot-password-code-input"
            aria-label="Enter your verification code"
            value={verificationCode}
            onChange={e => setVerificationCode(e.target.value)}
            required
          />
          <p>Please enter your new password:</p>
          <Password
            placeholder="New Password"
            data-testid="forgot-password-new-password-input"
            aria-label="Enter your new password"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            toggleMask
            required
            feedback={false}
            pt={{ hideIcon: { className: S.pwIcon }, showIcon: { className: S.pwIcon } }}
          />
          <p>Confirm your new password:</p>
          <Password
            placeholder="Confirm Password"
            data-testid="forgot-password-confirm-password-input"
            aria-label="Confirm your new password"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            toggleMask
            required
            feedback={false}
            pt={{ hideIcon: { className: S.pwIcon }, showIcon: { className: S.pwIcon } }}
          />
          {passwordError && <p className="error-text">{passwordError}</p>}
          <div className={S.buttonContainer}>
            <Button
              className={S.actionButton}
              data-testid="forgot-password-back-button"
              severity="secondary"
              onClick={() => navigate(LoginAppRoutes.route)}
              label="Back to Login"
              type="button"
            />
            <Button
              className={S.actionButton}
              data-testid="forgot-password-reset-button"
              label="Reset Password"
              type="submit"
              loading={isRequestInFlight}
              disabled={isRequestInFlight}
            />
          </div>
        </form>
      )}
    </LoginLayout>
  );
}
